/**
 * Hooks for scheduled meetings (TRUS-691): the API in context (staff by
 * default, the member portal provides memberMeetingsApi), the staff gate, a
 * meeting's live state, the upcoming list, and Join (which hands the call to
 * the existing call panel through callStore).
 */

import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAppTranslation } from '@/contexts/TranslationContext';
import useActiveOrgId from '@/hooks/useActiveOrgId';
import { CallsApiError } from '@/services/callsApi';
import { DISABLED_MEETINGS_CONFIG, MeetingsApiError, meetingsApi, type ListMeetingsParams, type Meeting, type MeetingsApi } from '@/services/meetingsApi';
import { formatMeetingRange } from './meetingTime';
import { callStore, useCallStore } from '../calls/callStore';

export const MeetingsApiContext = createContext<MeetingsApi>(meetingsApi);
export const useMeetingsApi = () => useContext(MeetingsApiContext);

/** The org capability that turns meetings on (requires messaging_calls). */
export const MEETINGS_CAPABILITY = 'messaging_meetings';

/** GET /meetings/config — `{enabled, can_schedule}` for this org and caller. */
export function useMeetingsConfig() {
  const api = useMeetingsApi();
  const orgId = useActiveOrgId();
  const q = useQuery({
    queryKey: ['meetings-config', api.kind, orgId],
    queryFn: () => api.getConfig(),
    staleTime: 60_000,
    retry: false,
  });
  return q.data ?? DISABLED_MEETINGS_CONFIG;
}

/** Staff may schedule (the server's `can_schedule`; never in the member portal). */
export function useCanScheduleMeetings(): boolean {
  const api = useMeetingsApi();
  const config = useMeetingsConfig();
  return api.kind === 'staff' && config.can_schedule;
}

export const meetingKey = (kind: string, id: number | null | undefined) => ['meeting', kind, id] as const;
export const MEETINGS_LIST_KEY = 'meetings-upcoming';

/** One meeting's live state (the card prefers it over the message metadata). */
export function useMeeting(id: number | null | undefined, enabled = true) {
  const api = useMeetingsApi();
  return useQuery({
    queryKey: meetingKey(api.kind, id),
    queryFn: () => api.get(id as number),
    enabled: enabled && id != null,
    staleTime: 15_000,
    // Re-read while it can still change state (starts, goes live, ends).
    refetchInterval: (q) => {
      const m = q.state.data as Meeting | undefined;
      return m && (m.status === 'cancelled' || m.status === 'ended') ? false : 30_000;
    },
    retry: false,
  });
}

/** Upcoming meetings for the caller (optionally one conversation's). */
export function useUpcomingMeetings(params: Omit<ListMeetingsParams, 'from'> & { enabled?: boolean } = {}) {
  const api = useMeetingsApi();
  const orgId = useActiveOrgId();
  const { enabled = true, ...rest } = params;
  return useQuery({
    queryKey: [MEETINGS_LIST_KEY, api.kind, orgId, rest.channelId ?? null, !!rest.mine, rest.to ?? null],
    queryFn: async () => {
      // From an hour ago: a meeting that started late is still "upcoming / live".
      const from = new Date(Date.now() - 60 * 60_000).toISOString();
      const list = await api.list({ ...rest, from });
      return list
        .filter((m) => m.status !== 'cancelled' && m.status !== 'ended')
        .sort((a, b) => a.starts_at.localeCompare(b.starts_at));
    },
    enabled,
    staleTime: 30_000,
    refetchInterval: enabled ? 60_000 : false,
    retry: false,
  });
}

/** A clock that ticks (so Join turns on at joinable_from without a refetch). */
export function useNow(intervalMs = 30_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}

/** Keep every cached copy of a meeting fresh after a change. */
export function useMeetingCache() {
  const api = useMeetingsApi();
  const queryClient = useQueryClient();
  return useCallback(
    (m: Meeting | null | undefined, opts: { channelId?: number | null } = {}) => {
      if (m) queryClient.setQueryData(meetingKey(api.kind, m.id), m);
      queryClient.invalidateQueries({ queryKey: [MEETINGS_LIST_KEY] });
      const channelId = m?.channel_id ?? opts.channelId;
      if (channelId != null) queryClient.invalidateQueries({ queryKey: ['channel-messages', channelId] });
    },
    [api.kind, queryClient],
  );
}

/** Join: POST /meetings/{id}/join, then the existing call panel takes over. */
export function useJoinMeeting() {
  const { t, language } = useAppTranslation();
  const api = useMeetingsApi();
  const { session } = useCallStore();
  const update = useMeetingCache();
  const mutation = useMutation({
    mutationFn: (m: Meeting) => api.join(m.id),
    onSuccess: (join, m) => {
      const channelId = join.call.channel_id ?? m.channel_id ?? 0;
      callStore.open({
        callId: join.call.id,
        channelId,
        mode: join.mode,
        join,
        channelName: m.title || undefined,
        // A meeting without a conversation, or one the caller cannot read, opens the panel alone.
        standalone: !m.channel_id || join.call.can_open_channel === false,
        meetingId: m.id,
      });
      update({ ...m, status: 'live', call_id: join.call.id });
    },
    onError: (err: unknown, m) => {
      const e = err instanceof CallsApiError ? err : null;
      // The meeting's call records: ask for consent, then join it like any call (calls v2).
      if (e?.code === 'consent_required' && e.callId) {
        callStore.open({
          callId: e.callId,
          channelId: m.channel_id ?? 0,
          mode: m.mode,
          channelName: m.title || undefined,
          consentNeeded: true,
          standalone: !m.channel_id,
          meetingId: m.id,
        });
        return;
      }
      const joinableFrom = err instanceof MeetingsApiError ? err.joinableFrom : null;
      const opensAt = joinableFrom ? formatMeetingRange(joinableFrom, null, language) : null;
      // The server's message is already plain en / es; ours are the fallback.
      const description =
        e?.code === 'meeting_not_open'
          ? opensAt
            ? t('messaging.meetings.notOpenAt', 'You can join from {{time}}.', { time: opensAt })
            : e.message || t('messaging.meetings.tooEarly', 'Join opens 10 minutes before the meeting starts.')
          : e?.code === 'meeting_cancelled'
            ? e.message || t('messaging.meetings.wasCancelled', 'This meeting was cancelled.')
            : e?.code === 'meeting_ended' || e?.code === 'call_ended'
              ? e.message || t('messaging.meetings.hasEnded', 'This meeting has ended.')
              : e?.code === 'not_found' || e?.status === 404
                ? t('messaging.meetings.linkUnavailable', "This meeting isn't available to you, or it no longer exists.")
                : e?.code === 'video_unavailable'
                  ? e.message || t('messaging.calls.unavailable', 'Calls are not available right now. Try again later.')
                  : e?.code === 'calls_disabled'
                    ? t('messaging.calls.disabled', 'Calls are turned off for this organization.')
                    : (err as Error)?.message;
      // A finished / cancelled meeting: refresh the card so it says so.
      if (e?.code === 'meeting_cancelled' || e?.code === 'meeting_ended') update(null, { channelId: m.channel_id });
      toast.error(t('messaging.meetings.joinFailed', 'Could not join the meeting'), { description });
    },
  });
  const join = useCallback(
    (m: Meeting) => {
      if (m.call_id != null && session?.callId === m.call_id) return;
      mutation.mutate(m);
    },
    [mutation, session?.callId],
  );
  return { join, isPending: mutation.isPending, pendingId: mutation.isPending ? mutation.variables?.id ?? null : null };
}

/** Copy text; true when it worked. */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/** Save a Blob as a file (the ICS download). */
export function saveBlob(blob: Blob, filename: string) {
  try {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch {
    /* the browser refused; nothing else to do */
  }
}
